import { deflateSync, inflateSync, strFromU8, strToU8 } from 'fflate';
import _isPlainObject from 'lodash-es/isPlainObject';
import _range from 'lodash-es/range';
import _uniq from 'lodash-es/uniq';

import { defer } from './Promise';

function compress(data: string): string {
  const compressed = deflateSync(strToU8(data));
  let binary = '';
  for (let i = 0; i < compressed.length; i++) {
    binary += String.fromCharCode(compressed[i]);
  }

  return btoa(binary);
}

function decompress(data: string): string {
  try {
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return strFromU8(inflateSync(bytes));
  } catch {
    // Fallback: return raw value for any legacy uncompressed entries
    return data;
  }
}

export function hasItem(key: string): boolean {
  let result = false;

  if (window.Modernizr.localstorage) {
    result = window.localStorage.getItem(key) !== null;
  }

  if (!result && window.Modernizr.sessionstorage) {
    result = window.sessionStorage.getItem(key) !== null;
  }

  return result;
}

export function getItem<T>(key: string, isCompressed?: boolean): T {
  let item: string = null;

  if (window.Modernizr.localstorage) {
    item = window.localStorage.getItem(key);
  }

  if (item === null && window.Modernizr.sessionstorage) {
    item = window.sessionStorage.getItem(key);
  }

  if (item === null) {
    return null;
  }

  const decompressed = isCompressed ? decompress(item) : item;

  if (!isJsonString(decompressed)) {
    return null;
  }

  return JSON.parse(decompressed);
}

export function setItem(key: string, item: any, persist?: boolean, compressData?: boolean): void {

  if (_isPlainObject(item) || Array.isArray(item)) {
    item = JSON.stringify(item);
  }

  if (compressData) {
    const raw = String(item);
    // const uncompressedBytes = new Blob([raw]).size;

    item = compress(raw);
  }

  // const compressedBytes = new Blob([item]).size;
  // const ratio = ((1 - compressedBytes / uncompressedBytes) * 100).toFixed(1);

  // const localStorageLimit = 5 * 1024 * 1024;
  // const usedBytes = Object.keys(window.localStorage).reduce((total, k) => {
  //   return total + new Blob([window.localStorage.getItem(k)]).size + new Blob([k]).size;
  // }, 0);
  // const remainingBytes = localStorageLimit - usedBytes;

  // console.log(
  //   `[Storage] setItem("${key}") | ` +
  //   `Uncompressed: ${uncompressedBytes.toLocaleString()} B | ` +
  //   `Compressed: ${compressedBytes.toLocaleString()} B | ` +
  //   `Saved: ${ratio}%`
  // );

  if (persist && window.Modernizr.localstorage) {
    window.localStorage.setItem(key, item);

    if (window.Modernizr.sessionstorage) {
      window.sessionStorage.removeItem(key);
    }
  } else if (window.Modernizr.sessionstorage) {
    window.sessionStorage.setItem(key, item);

    if (window.Modernizr.localstorage) {
      window.localStorage.removeItem(key);
    }
  }
}

export function addOrGetItemAsync<T>(key: string, promiseFn: () => Promise<T>, persist?: boolean, compressData?: boolean): Promise<T> {
  const deferred = defer<T>();

  if (hasItem(key)) {
    deferred.resolve(getItem<T>(key, compressData));
  } else {
    promiseFn().then((item) => {
      try {
        setItem(key, item, persist, compressData);
      } catch (err) {
        // Note: It's important to handle exceptions as it's possible we exceed the browser's quota for storage (5mb for Chrome).
        console.log('setItem failed!', err);
      }
      deferred.resolve(item);
    }, (err) => {
      deferred.reject(err);
    }).catch((err) => {
      deferred.reject(err);
    });
  }

  return deferred.promise;
}

export function removeItem(key: string): void {
  if (window.Modernizr.sessionstorage) {
    window.sessionStorage.removeItem(key);
  }

  if (window.Modernizr.localstorage) {
    window.localStorage.removeItem(key);
  }
}

export function getKeys(): string[] {
  const keys = [].concat(
    getSessionStorageKeys(),
    getLocalStorageKeys());

  return _uniq(keys);
}

export function clear(): void {
  window.Modernizr.localstorage && window.localStorage.clear();
  window.Modernizr.sessionstorage && window.sessionStorage.clear();
}

function getLocalStorageKeys(): string[] {
  let keys: string[] = [];

  if (window.Modernizr.localstorage) {
    keys = _range(window.localStorage.length).map((x) => window.localStorage.key(x));
  }

  return keys;
}

function getSessionStorageKeys(): string[] {
  let keys: string[] = [];

  if (window.Modernizr.sessionstorage) {
    keys = _range(window.sessionStorage.length).map((x) => window.sessionStorage.key(x));
  }

  return keys;
}

function isJsonString(raw: string): boolean {
  try {
    JSON.parse(raw);
  } catch (e) {
    return false;
  }
  return true;
}
